// Single source of truth for roles, plans, credits and limits.
// Read by both server (enforcement) and client (display) - never duplicated (§5, §8.5).
export const ROLES = ['owner', 'manager', 'front_desk', 'installer'];
export const INVITABLE_ROLES = ['manager', 'front_desk', 'installer'];
export const PLANS = ['trial', 'starter', 'pro', 'enterprise'];
export const TRIAL_DAYS = 14;
export const TRIAL_INITIAL_CREDITS = 10;
export const PLAN_DEFINITIONS = {
    trial: {
        plan: 'trial',
        label: 'Trial',
        priceMonthlyCents: 0,
        priceYearlyCents: 0,
        seats: 3,
        aiCreditsPerMonth: TRIAL_INITIAL_CREDITS,
        smsCreditsPerMonth: 0,
        twilioNumberLimit: 0,
        assistantMessagesPerDay: 10,
        tagline: '14 days free, no card required',
        features: ['Full features during trial', `${TRIAL_INITIAL_CREDITS} AI mockups (one-time)`, 'Read-only after trial ends', 'AI assistant - 10 messages / day'],
        gates: { csvExport: false, auditLogUi: false, hdRenders: false, multiLocation: false, prioritySupport: false, smsMessaging: false, reviewRequests: false, outboundWebhooks: false, sharedSmsNotifications: false },
    },
    starter: {
        plan: 'starter',
        label: 'Starter',
        priceMonthlyCents: 6900,
        priceYearlyCents: 6900 * 10,
        seats: 3,
        aiCreditsPerMonth: 40,
        smsCreditsPerMonth: 0,
        twilioNumberLimit: 0,
        assistantMessagesPerDay: 15,
        tagline: 'For the one-shop operator getting started',
        features: ['1 shop · 3 seats', '40 AI mockups / mo', 'Unlimited leads & quotes', 'Stripe deposits', 'AI assistant - 15 messages / day'],
        gates: { csvExport: false, auditLogUi: false, hdRenders: false, multiLocation: false, prioritySupport: false, smsMessaging: false, reviewRequests: true, outboundWebhooks: false, sharedSmsNotifications: true },
    },
    pro: {
        plan: 'pro',
        label: 'Pro',
        priceMonthlyCents: 12900,
        priceYearlyCents: 12900 * 10,
        seats: 10,
        aiCreditsPerMonth: 150,
        smsCreditsPerMonth: 300,
        twilioNumberLimit: 1,
        assistantMessagesPerDay: 30,
        mostPopular: true,
        tagline: 'For busy shops with a team',
        features: ['1 shop · 10 seats', '150 AI mockups / mo', 'HD mockup renders', 'Reports CSV export + audit log', 'Priority support', 'AI assistant - 30 messages / day'],
        gates: { csvExport: true, auditLogUi: true, hdRenders: true, multiLocation: false, prioritySupport: true, smsMessaging: true, reviewRequests: true, outboundWebhooks: false, sharedSmsNotifications: true },
    },
    enterprise: {
        plan: 'enterprise',
        label: 'Turbo',
        priceMonthlyCents: 24900,
        priceYearlyCents: 24900 * 10,
        seats: null,
        aiCreditsPerMonth: 400,
        smsCreditsPerMonth: 1000,
        twilioNumberLimit: 1,
        assistantMessagesPerDay: 60,
        tagline: 'For multi-location operators',
        features: ['Multiple shops, one owner view', '400 pooled AI mockups / mo', 'Unlimited seats', 'Cross-shop reports', 'Dedicated onboarding', 'AI assistant - 60 messages / day'],
        gates: { csvExport: true, auditLogUi: true, hdRenders: true, multiLocation: true, prioritySupport: true, smsMessaging: true, reviewRequests: true, outboundWebhooks: true, sharedSmsNotifications: true },
    },
};
export const SMS_TOPUP_CREDITS = 200;
export const SMS_TOPUP_PRICE_CENTS = 1000;
export const TOPUP_CREDITS = 50;
export const TOPUP_PRICE_CENTS = 1500;
export const AI_CREDIT_COST = {
    standard: 1,
    hd: 3,
};
export const LOW_CREDITS_THRESHOLD = 5;
export const LOW_SMS_CREDITS_THRESHOLD = 20;
// §33 Stage 4 - anti-abuse daily/per-minute send caps, on top of (not
// instead of) the sms credits meter above. Defaults only - a messaging
// profile's own daily_send_cap column overrides these per workspace.
export const SMS_DAILY_CAP_DEDICATED = 200;
export const SMS_DAILY_CAP_SHARED = 50;
export const SMS_PER_MINUTE_LIMIT = 10;
export const DEFAULT_LABOR_RATE_CENTS = 9500;
export const DEFAULT_ROUNDING_CENTS = 2500;
export const DEFAULT_DEPOSIT_PCT = 0;
export const DEFAULT_QUOTE_VALIDITY_DAYS = 14;
export const MIN_VEHICLE_YEAR = 1950;
// Evaluated per-call so it stays correct even on a long-lived client bundle.
export const maxVehicleYear = () => new Date().getFullYear() + 2;
export const QUOTE_STATUSES = [
    'draft', 'sent', 'viewed', 'deposit_paid', 'converted', 'declined', 'expired', 'void',
];
export const JOB_STATUSES = [
    'unscheduled', 'scheduled', 'in_progress', 'completed', 'closed', 'cancelled',
];
export const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'quoted', 'won', 'lost'];
// 'phone_call' and 'sms' are the §33 dedicated-number auto-capture sources
// (0076 widened the DB check to match).
export const LEAD_SOURCES = ['instagram', 'whatsapp', 'website', 'walk_in', 'referral', 'phone', 'phone_call', 'sms', 'other'];
export const INVENTORY_UNITS = ['sqft', 'roll', 'ft', 'each', 'bottle', 'ml'];
// Mirrors the §29 ServiceKind union (schemas/catalog.ts) plus a catch-all
// 'other' bucket for consumables/tools that don't tie to a service's material
// catalog - duplicated here rather than imported to avoid a constants ->
// schemas import cycle (schemas/catalog.ts already imports from here).
export const INVENTORY_CATEGORIES = ['wrap', 'ppf', 'tint', 'ceramic', 'print', 'other'];
export const INVENTORY_CATEGORY_LABELS = {
    wrap: 'Vinyl Wrap',
    ppf: 'Paint Protection Film',
    tint: 'Window Tint',
    ceramic: 'Ceramic Coating',
    print: 'Print Media & Laminate',
    other: 'Other',
};
/** Categories with one fixed unit of measure - the Inventory UI locks the
 * unit field to this value and hides the picker. 'other' has no entry, so
 * its unit stays a free-form pick from INVENTORY_UNITS. */
export const INVENTORY_CATEGORY_UNIT = {
    wrap: 'sqft',
    ppf: 'sqft',
    tint: 'sqft',
    ceramic: 'bottle',
    print: 'sqft',
};
export const PAGE_SIZE_DEFAULT = 25;
export const PAGE_SIZE_MAX = 100;
// ---------------------------------------------------------------------------
// §29 pricing-engine enumerations (mirror the `pricing/types.ts` unions, so
// server seed logic and the SPA's optimistic preview share one source of truth
// for the shape - live values come from the DB-resolved profile at request time).
// ---------------------------------------------------------------------------
export const MODIFIER_KEYS = [
    'side_skirts',
    'front_splitter',
    'front_lip',
    'rear_diffuser',
    'trunk_spoiler',
    'fixed_wing',
    'roof_spoiler',
    'widebody_flares',
    'vented_hood',
    'complex_bumper',
    'sensor_suite',
    'flush_handles',
    'roof_rails',
    'running_boards',
    'aftermarket_body_kit',
];
export const FINISH_KEYS = [
    'gloss',
    'satin_matte',
    'metallic_pearl',
    'textured_directional',
    'color_shift',
    'chrome',
];
// Wrap film product names virtually always carry their finish word
// ("Gloss Boat Blue", "Satin Flip Volcanic Flare", "Matte Metallic Charcoal").
// The quote builder derives the line's finish class from the selected
// inventory item's name instead of asking for it separately - checked most
// specific first, defaulting to gloss (the baseline class) when no keyword
// matches.
const FINISH_NAME_PATTERNS = [
    ['chrome', /\bchrome\b/],
    ['color_shift', /\b(shift|flip|chameleon|iridescent)\b/],
    ['textured_directional', /\b(brushed|carbon|textured|honeycomb|camo)\b/],
    ['metallic_pearl', /\b(metallic|pearl)\b/],
    ['satin_matte', /\b(satin|matte|matt|frozen)\b/],
];
export function deriveFinishKeyFromName(name) {
    const normalized = name.toLowerCase();
    for (const [key, pattern] of FINISH_NAME_PATTERNS) {
        if (pattern.test(normalized))
            return key;
    }
    return 'gloss';
}
export const SIZE_CLASSES = [
    'city_compact',
    'sedan',
    'hatch_wagon',
    'coupe_sports',
    'crossover_mid_suv',
    'full_suv',
    'pickup_truck',
    'van_commercial',
    'exotic_supercar',
];
/** Coarser 3-way size grouping used by the tint/ceramic flat price matrices
 * (tint_price_matrix / ceramic_package_tiers), as opposed to the 9-way
 * SIZE_CLASSES used by the vehicle-aware wrap/PPF engine. */
export const SIZE_BUCKETS = ['coupe_sedan', 'suv_truck', 'van_oversize'];
export const SIZE_CLASS_TO_SIZE_BUCKET = {
    city_compact: 'coupe_sedan',
    sedan: 'coupe_sedan',
    hatch_wagon: 'coupe_sedan',
    coupe_sports: 'coupe_sedan',
    crossover_mid_suv: 'suv_truck',
    full_suv: 'suv_truck',
    pickup_truck: 'suv_truck',
    van_commercial: 'van_oversize',
    exotic_supercar: 'van_oversize',
};
export const DISASSEMBLY_LEVELS = ['L0', 'L1', 'L2', 'L3'];
/** Platform DEFAULT disassembly minutes by level (§29.2). The workspace may
 * override these in the DB; this is the shape/fallback both server seed and
 * client preview share. */
export const DEFAULT_DISASSEMBLY_MINUTES = {
    L0: 0,
    L1: 180,
    L2: 480,
    L3: 900,
};
/** Platform DEFAULT door-jambs additions (§29.2). Workspace-overridable. */
export const DEFAULT_DOOR_JAMBS_MINUTES = 420;
export const DEFAULT_DOOR_JAMBS_FT_X10 = 100;
/** Platform DEFAULT labor minutes per condition/defect panel (§29.2 paint-
 * correction time). Matches the golden-vector fixture in the engine tests.
 * There is no workspace/DB override table for this in the current schema, so
 * the server recompute derives defect labor as
 * `defectPanelCount × DEFAULT_DEFECT_MINUTES_PER_PANEL` from this constant. */
export const DEFAULT_DEFECT_MINUTES_PER_PANEL = 30;
export const DISASSEMBLY_LEVEL_COPY = [
    { id: 'L0', name: 'No disassembly', description: 'The wrap goes on over trim with edges tucked in place. Fastest option, lowest cost.' },
    { id: 'L1', name: 'Light disassembly', description: 'Door handles, badges, and mirror caps come off first for cleaner edges.' },
    { id: 'L2', name: 'Moderate disassembly', description: 'Bumpers, lights, and additional trim come off for a closer-to-factory finish.' },
    { id: 'L3', name: 'Full disassembly', description: 'Bumpers, lights, handles, and moldings are fully removed for the cleanest possible finish.' },
];
export const CONDITION_TIERS = ['excellent', 'good', 'fair', 'poor'];
export const CONDITION_TIER_COPY = [
    { id: 'excellent', name: 'Excellent', description: 'No dents, rust, or paint damage. Ready to wrap as-is.' },
    { id: 'good', name: 'Good', description: 'Minor scuffs or small chips. Nothing that needs repair first.' },
    { id: 'fair', name: 'Fair', description: 'Some dents, scratches, or faded paint the shop should look at first.' },
    { id: 'poor', name: 'Poor', description: 'Noticeable damage, rust, or bodywork needed before a wrap will look right.' },
];
// RemovalTier itself is not invented here - it already existed as the
// New Quote flow's removal-difficulty enum (packages/shared/src/pricing/
// types.ts). Moved here so both that engine and this widget share one
// definition, matching how DISASSEMBLY_LEVELS is the single source for
// both sides already; pricing/types.ts re-exports it rather than
// redefining it.
export const REMOVAL_TIERS = ['none', 'light', 'moderate', 'heavy_baked'];
export const REMOVAL_TIER_COPY = [
    { id: 'none', name: 'None', description: "No existing wrap or PPF to remove - it's a clean surface to start from." },
    { id: 'light', name: 'Light', description: 'Recent film that peels off easily, with little to no residue.' },
    { id: 'moderate', name: 'Moderate', description: 'Older film with some adhesive residue left behind.' },
    { id: 'heavy_baked', name: 'Heavy / baked-on', description: 'Sun-baked film that needs heavy scraping to remove.' },
];
export const FINISH_FAMILIES = ['gloss', 'satin', 'matte', 'color_shift', 'chrome', 'textured', 'not_sure'];
export const FINISH_FAMILY_COPY = [
    { id: 'gloss', name: 'Gloss', description: 'Reflective, high-shine finish - the classic wrap look.' },
    { id: 'satin', name: 'Satin', description: 'Soft sheen, between gloss and matte.' },
    { id: 'matte', name: 'Matte', description: 'Flat, non-reflective finish.' },
    { id: 'color_shift', name: 'Color-shift', description: 'Changes hue depending on light and viewing angle.' },
    { id: 'chrome', name: 'Chrome', description: 'Mirror-like metallic finish.' },
    { id: 'textured', name: 'Textured', description: 'Directional or patterned texture you can feel, not just see.' },
    { id: 'not_sure', name: 'Not sure', description: "Let {shop} recommend a finish." },
];
/** FinishFamily -> the §29 engine's FinishKey, where a reasonable 1:1
 * mapping exists (§31.10 draft-prefill conversion). `null` means the family
 * doesn't resolve to one engine finish (satin/matte share one FinishKey;
 * "not sure" resolves to none) and the estimator picks it manually. */
export const FINISH_FAMILY_TO_FINISH_KEY = {
    gloss: 'gloss',
    satin: 'satin_matte',
    matte: 'satin_matte',
    color_shift: 'color_shift',
    chrome: 'chrome',
    textured: 'textured_directional',
    not_sure: null,
};
/** Coarse, consumer-facing panel areas for the widget's partial-coverage
 * picker (§31.5 step 2, §31.6 signature element). Deliberately coarser than
 * the engine/DB's `panel_defs.panel_key` values (hood/roof/doors/fenders/
 * etc, §29.3) - the widget never talks to panel_defs (that's an
 * authenticated-only reference table, §31.4), so these are carried through
 * QuoteDraftPrefill as flagged raw strings for the estimator to resolve
 * against the real panel catalog (§31.10), not resolved here. */
export const EMBED_PANEL_AREAS = [
    'hood',
    'roof',
    'trunk_hatch',
    'front_bumper',
    'rear_bumper',
    'left_side',
    'right_side',
    'mirrors',
    'pillars_chrome_delete',
    'other',
];
/** The widget's disassembly/condition questions each add one extra choice
 * ("no preference" / "unsure") on top of the §29 ids - single source for the
 * zod schema so the extra choice string isn't retyped there too. */
export const EMBED_DISASSEMBLY_CHOICES = [...DISASSEMBLY_LEVELS, 'no_preference'];
export const EMBED_CONDITION_CHOICES = [...CONDITION_TIERS, 'unsure'];
// ---------------------------------------------------------------------------
// §31 Phase 31-F multi-service quotes - the widget now asks its coverage/
// material questions per service rather than assuming wrap. QUOTE_SERVICES
// is the same four-key set the wrap directory already uses for
// directory_shops.services (directory/src/lib/services.ts) - kept identical
// rather than imported, since the directory package doesn't depend on
// packages/shared and this is a small enough set to duplicate deliberately.
// ---------------------------------------------------------------------------
export const QUOTE_SERVICES = ['wrap', 'tint', 'ppf', 'ceramic', 'print'];
export const QUOTE_SERVICE_COPY = [
    { id: 'wrap', name: 'Color wrap', description: 'Full or partial vinyl color change for your vehicle.' },
    { id: 'tint', name: 'Window tint', description: 'Window film for privacy, heat rejection, and a cleaner look.' },
    { id: 'ppf', name: 'Paint protection film', description: 'Clear film that guards your paint from rock chips and road debris.' },
    { id: 'ceramic', name: 'Ceramic coating', description: 'A durable coating that adds gloss and makes washing easier.' },
    { id: 'print', name: 'Printed wrap', description: 'Custom printed graphics, branding, or artwork wrapped on your vehicle.' },
];
export const PPF_COVERAGE_CHOICES = ['partial_front', 'full_front', 'track_pack', 'full_car', 'custom', 'not_sure'];
export const PPF_COVERAGE_COPY = [
    { id: 'partial_front', name: 'Partial front', description: 'Hood edge, mirrors, and front bumper edges - the most exposed areas.' },
    { id: 'full_front', name: 'Full front', description: 'Full hood, fenders, mirrors, and the complete front bumper.' },
    { id: 'track_pack', name: 'Track pack', description: 'Full front plus rocker panels and rear wheel arches, for track or spirited driving.' },
    { id: 'full_car', name: 'Full car', description: 'Every painted panel covered edge to edge.' },
    { id: 'custom', name: 'Custom', description: 'Tell {shop} exactly which panels you want covered.' },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} recommend coverage.' },
];
export const PPF_FINISH_CHOICES = ['gloss', 'matte', 'not_sure'];
export const PPF_FINISH_COPY = [
    { id: 'gloss', name: 'Gloss', description: 'Invisible, high-shine finish that matches a glossy paint job.' },
    { id: 'matte', name: 'Matte', description: "Flat finish for matte or satin-wrapped paint." },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} recommend a finish.' },
];
export const TINT_FILM_CHOICES = ['ceramic', 'carbon', 'dyed', 'not_sure'];
export const TINT_FILM_COPY = [
    { id: 'ceramic', name: 'Ceramic', description: "Best heat rejection and clarity, with no signal interference - the premium option." },
    { id: 'carbon', name: 'Carbon', description: "Won't fade over time and blocks a good amount of heat." },
    { id: 'dyed', name: 'Dyed', description: 'A budget-friendly option for privacy and glare reduction.' },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} recommend a film.' },
];
// VLT (visible light transmission) percentages, not an ordinal scale, so the
// ids are the percentage strings themselves rather than a made-up key.
export const TINT_SHADE_CHOICES = ['5', '20', '35', '50', '70', 'not_sure'];
export const TINT_SHADE_COPY = [
    { id: '5', name: '5%', description: 'Very dark - limo tint.' },
    { id: '20', name: '20%', description: 'Dark, a popular stealth look.' },
    { id: '35', name: '35%', description: 'Medium shade, a common daily-driver choice.' },
    { id: '50', name: '50%', description: 'Light tint, mostly for heat and glare reduction.' },
    { id: '70', name: '70%', description: 'Lightest tint, barely visible.' },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} recommend a shade.' },
];
export const TINT_AREA_CHOICES = [
    'front_two',
    'rear_sides_and_back',
    'full_car',
    'windshield_strip',
    'full_windshield',
    'sunroof',
    'other',
];
export const TINT_AREA_COPY = [
    { id: 'front_two', name: 'Front two windows', description: 'Driver and front passenger windows only.' },
    { id: 'rear_sides_and_back', name: 'Rear sides and back', description: 'Rear door windows and the back windshield.' },
    { id: 'full_car', name: 'Full car', description: 'Every window, front to back.' },
    { id: 'windshield_strip', name: 'Windshield strip', description: 'A narrow strip along the top of the windshield.' },
    { id: 'full_windshield', name: 'Full windshield', description: 'The entire windshield.' },
    { id: 'sunroof', name: 'Sunroof', description: 'Sunroof or moonroof glass.' },
    { id: 'other', name: 'Other', description: 'Tell {shop} which windows you want covered.' },
];
export const TINT_EXISTING_CHOICES = ['none', 'existing_to_remove', 'not_sure'];
export const TINT_EXISTING_COPY = [
    { id: 'none', name: 'None', description: 'No existing tint on the vehicle.' },
    { id: 'existing_to_remove', name: 'Existing tint to remove', description: 'Old tint needs to come off first.' },
    { id: 'not_sure', name: 'Not sure', description: "Not sure whether there's existing tint." },
];
export const CERAMIC_PACKAGE_CHOICES = ['entry', 'multi_year', 'premium', 'not_sure'];
export const CERAMIC_PACKAGE_COPY = [
    { id: 'entry', name: 'Entry', description: '1-2 year protection, a durable step up from wax.' },
    { id: 'multi_year', name: 'Multi-year', description: '3-5 year protection for regular drivers.' },
    { id: 'premium', name: 'Premium', description: 'The longest-lasting coating with the deepest gloss.' },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} recommend a package.' },
];
export const CERAMIC_CORRECTION_CHOICES = ['none', 'one_step', 'two_step', 'not_sure'];
export const CERAMIC_CORRECTION_COPY = [
    { id: 'none', name: 'None', description: 'Paint is already in good shape - coat as-is.' },
    { id: 'one_step', name: 'One-step polish', description: 'A single pass to remove light swirls and add gloss before coating.' },
    { id: 'two_step', name: 'Two-step correction', description: 'A deeper polish to remove heavier scratches and oxidation before coating.' },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} inspect and recommend.' },
];
export const CERAMIC_ADDON_CHOICES = ['interior', 'glass', 'wheels', 'trim'];
export const CERAMIC_ADDON_COPY = [
    { id: 'interior', name: 'Interior', description: 'Seats, carpets, and interior plastics.' },
    { id: 'glass', name: 'Glass', description: 'Windshield and windows, for easier cleaning.' },
    { id: 'wheels', name: 'Wheels', description: 'Wheel faces and barrels.' },
    { id: 'trim', name: 'Trim', description: 'Exterior plastic and rubber trim.' },
];
export const PRINT_COVERAGE_CHOICES = ['full', 'three_quarter', 'half', 'spot', 'not_sure'];
export const PRINT_COVERAGE_COPY = [
    { id: 'full', name: 'Full wrap', description: 'Printed graphics covering the entire vehicle.' },
    { id: 'three_quarter', name: 'Three-quarter wrap', description: 'Printed graphics covering most panels, leaving some factory paint showing.' },
    { id: 'half', name: 'Half wrap', description: 'Printed graphics on roughly half the vehicle, such as the lower panels or one side.' },
    { id: 'spot', name: 'Spot graphics or lettering', description: 'Logos, lettering, or graphics on specific panels only.' },
    { id: 'not_sure', name: 'Not sure', description: 'Let {shop} recommend coverage.' },
];
export const PRINT_ARTWORK_CHOICES = ['have_artwork', 'need_design', 'not_sure'];
export const PRINT_ARTWORK_COPY = [
    { id: 'have_artwork', name: 'I have print-ready artwork', description: 'You already have a logo or design file ready to print.' },
    { id: 'need_design', name: 'I need a design created', description: '{shop} will help create the artwork.' },
    { id: 'not_sure', name: 'Not sure', description: "Not sure yet - {shop} can help figure it out." },
];
/** Credits spent per printed-wrap AI action (§ printed wraps). Standard/HD
 * mirror AI_CREDIT_COST's mockup tiers; measure is a separate, cheaper action
 * (a text-model vehicle-dimension lookup, not an image render). */
export const PRINT_CREDIT_COST = { standard: 2, hd: 5, measure: 1, setStandard: 6, setHd: 10, outline: 4 };
/** How a render set's price splits across its three renders (side, front,
 * rear), used to record credits_spent per row and to refund exactly one
 * row's share when only that render fails. Each quality's shares sum to
 * its set price. */
export const PRINT_RENDER_SET_SHARES = {
    standard: { side: 2, front: 2, rear: 2 },
    hd: { side: 4, front: 3, rear: 3 },
};
/** Bumped whenever the widget's copy/enum bundle changes (disassembly/
 * condition/removal/finish-family ids or copy) so `GET .../config` (§31.4 #4)
 * gives the embedded widget a cheap way to detect it's showing stale
 * choices. */
export const EMBED_ENUM_BUNDLE_VERSION = 3;
export function roundUpTo(step, value) {
    return Math.ceil(value / step) * step;
}
export function formatMoney(cents, currency = 'usd', locale = 'en-US') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}
//# sourceMappingURL=constants.js.map